import React, { useEffect, useState, useCallback } from 'react';
import api from '../../../services/api';
import AlertToast from '../../../components/common/AlertToast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DEFAULT_CLASSES = ['Pre-Primary', 'LKG', 'UKG', 'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11 - Science', 'Class 11 - Commerce', 'Class 11 - Arts', 'Class 12 - Science', 'Class 12 - Commerce', 'Class 12 - Arts'];
const SECTIONS = ['A', 'B', 'C', 'D', 'E'];
const DEFAULT_SUBJECTS = ['Mathematics', 'Science', 'Physics', 'Chemistry', 'Biology', 'English', 'Tamil', 'Hindi', 'Social Studies', 'Computer Science', 'History', 'Geography', 'Economics', 'Commerce', 'Physical Education', 'Arts', 'Music', 'Environmental Science', 'Library', 'Moral Science', 'Free Period'];

const TIME_SLOTS = [
  '08:00 - 08:45', '08:45 - 09:30', '09:30 - 10:15',
  '10:15 - 10:30 (Break)', '10:30 - 11:15', '11:15 - 12:00', '12:00 - 12:45',
  '12:45 - 13:30 (Lunch)', '13:30 - 14:15', '14:15 - 15:00', '15:00 - 15:45',
];
const PERIOD_SLOTS = TIME_SLOTS.filter(s => !s.includes('Break') && !s.includes('Lunch'));

const blank = { class_grade: '', section: 'A', day: 'Monday', period_number: 1, subject: '', teacher_name: '', start_time: '', end_time: '', room: '', substitute_teacher: '', is_lab: false, lab_name: '' };

export default function TimetablePage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filterClass, setFilterClass] = useState('');
  const [filterSection, setFilterSection] = useState('A');
  const [viewMode, setViewMode] = useState('grid'); // grid | list
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [classOptions, setClassOptions] = useState(DEFAULT_CLASSES);
  const [sectionOptions, setSectionOptions] = useState(['A', 'B', 'C', 'D', 'E']);
  const [subjectOptions, setSubjectOptions] = useState(DEFAULT_SUBJECTS);
  const [labs, setLabs] = useState(['Physics Lab', 'Chemistry Lab', 'Biology Lab', 'Computer Lab', 'Language Lab']);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Load school config for classes
  useEffect(() => {
    api.get('/records/school_config', { params: { limit: 1 } })
      .then(r => {
        const rec = (r.data?.records || [])[0]?.recordData;
        if (rec) {
          if (rec.classes?.length) {
            setClassOptions(rec.classes);
          } else if (rec.total_classes) {
            const generated = [];
            const total = Number(rec.total_classes) || 12;
            for (let i = 1; i <= total; i++) generated.push(`Class ${i}`);
            if (generated.length > 0) setClassOptions(generated);
          }
          // Update subjects if configured
          if (rec.subjects?.length) {
            setSubjectOptions([...new Set([...rec.subjects, ...DEFAULT_SUBJECTS])]);
          }
          if (rec.sections?.length) setSectionOptions(rec.sections);
        }
      }).catch(() => {});
    // Load labs from lab_management
    api.get('/records/lab_management', { params: { limit: 20 } })
      .then(r => {
        const labRecords = r.data?.records || [];
        if (labRecords.length > 0) {
          setLabs(labRecords.map(l => l.recordData?.lab_name).filter(Boolean));
        }
      }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { limit: 200 };
    if (filterClass) params.class_grade = filterClass;
    if (filterSection) params.section = filterSection;
    const r = await api.get('/records/timetable', { params }).catch(() => ({ data: { records: [] } }));
    setRecords(r.data?.records || []);
    setLoading(false);
  }, [filterClass, filterSection]);

  useEffect(() => { load(); }, [load]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) await api.put(`/records/timetable/${editing}`, form);
      else await api.post('/records/timetable', form);
      setToast({ show: true, message: editing ? 'Period updated!' : 'Period added!', type: 'success' });
      setShowForm(false); setEditing(null); setForm(blank); load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Error', type: 'danger' });
    } finally { setSaving(false); }
  };

  const openEdit = (rec) => {
    setForm({ ...blank, ...rec.recordData });
    setEditing(rec._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this period?')) return;
    await api.delete(`/records/timetable/${id}`).catch(() => {});
    setToast({ show: true, message: 'Period removed', type: 'warning' }); load();
  };

  // Build grid: day → period → record
  const gridData = {};
  DAYS.forEach(d => { gridData[d] = {}; });
  records.forEach(rec => {
    const rd = rec.recordData || {};
    if (!gridData[rd.day]) return;
    gridData[rd.day][rd.period_number] = { ...rd, _id: rec._id };
  });

  const maxPeriods = Math.max(8, ...records.map(r => Number(r.recordData?.period_number) || 0));
  const periodNums = Array.from({ length: maxPeriods }, (_, i) => i + 1);

  const subjectColor = (subj) => {
    const colors = ['#6366f1', '#06b6d4', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#0ea5e9', '#84cc16', '#f97316'];
    const idx = (subj?.charCodeAt(0) || 0) % colors.length;
    return colors[idx];
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="mb-0 fw-bold" style={{ color: '#0f172a' }}>
            <i className="bi bi-calendar3 me-2 text-primary" />Timetable
          </h4>
          <div style={{ fontSize: 13, color: '#64748b' }}>Manage class schedules, subjects and faculty assignments</div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <div className="btn-group btn-group-sm" role="group">
            <button type="button" className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setViewMode('grid')}><i className="bi bi-grid-3x3 me-1" />Grid</button>
            <button type="button" className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setViewMode('list')}><i className="bi bi-list-ul me-1" />List</button>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => { setForm(filterClass ? { ...blank, class_grade: filterClass, section: filterSection } : blank); setEditing(null); setShowForm(s => !s); }}>
            <i className="bi bi-plus-lg me-1" />Add Period
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-center">
            <div className="col-sm-4 col-md-3">
              <select className="form-select form-select-sm" value={filterClass} onChange={e => setFilterClass(e.target.value)}>
                <option value="">All Classes</option>
                {classOptions.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-sm-3 col-md-2">
              <select className="form-select form-select-sm" value={filterSection} onChange={e => setFilterSection(e.target.value)}>
                <option value="">All Sections</option>
                {sectionOptions.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-auto">
              <button className="btn btn-outline-secondary btn-sm" onClick={load}><i className="bi bi-arrow-clockwise" /></button>
            </div>
            <div className="col-auto ms-auto">
              <span style={{ fontSize: 12, color: '#64748b' }}>{records.length} periods configured</span>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="card mb-3" style={{ border: '2px solid #e0e7ff', borderRadius: 12 }}>
          <div className="card-header d-flex justify-content-between" style={{ background: '#f5f3ff', borderBottom: '1px solid #e0e7ff' }}>
            <span className="fw-semibold" style={{ fontSize: 13 }}><i className="bi bi-plus-circle me-2 text-primary" />{editing ? 'Edit Period' : 'Add New Period'}</span>
            <button className="btn-close btn-sm" onClick={() => { setShowForm(false); setEditing(null); }} />
          </div>
          <div className="card-body">
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-md-3 col-6">
                  <label className="form-label">Class / Grade <span className="text-danger">*</span></label>
                  <select className="form-select" value={form.class_grade} onChange={e => f('class_grade', e.target.value)} required>
                    <option value="">Select class...</option>
                    {classOptions.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-md-2 col-6">
                  <label className="form-label">Section</label>
                  <select className="form-select" value={form.section} onChange={e => f('section', e.target.value)}>
                    {sectionOptions.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-md-3 col-6">
                  <label className="form-label">Day <span className="text-danger">*</span></label>
                  <select className="form-select" value={form.day} onChange={e => f('day', e.target.value)} required>
                    {DAYS.map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
                <div className="col-md-2 col-6">
                  <label className="form-label">Period No. <span className="text-danger">*</span></label>
                  <input type="number" className="form-control" min={1} max={12} value={form.period_number} onChange={e => f('period_number', Number(e.target.value))} required />
                </div>
                <div className="col-md-2 col-6">
                  <label className="form-label">Time Slot</label>
                  <select className="form-select" value={form.start_time} onChange={e => {
                    const parts = e.target.value.split(' - ');
                    f('start_time', parts[0] || ''); f('end_time', parts[1] || '');
                  }}>
                    <option value="">Select slot...</option>
                    {PERIOD_SLOTS.map(s => <option key={s} value={s.split(' - ')[0]}>{s}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Subject <span className="text-danger">*</span></label>
                  <select className="form-select" value={form.subject} onChange={e => f('subject', e.target.value)} required>
                    <option value="">Select subject...</option>
                    {subjectOptions.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Faculty Name <span className="text-danger">*</span></label>
                  <input className="form-control" value={form.teacher_name} onChange={e => f('teacher_name', e.target.value)} required placeholder="Teacher name" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Substitute Teacher</label>
                  <input className="form-control" value={form.substitute_teacher} onChange={e => f('substitute_teacher', e.target.value)} placeholder="If different teacher" />
                </div>
                <div className="col-md-3 col-6 d-flex align-items-end pb-1">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="isLab" checked={form.is_lab} onChange={e => f('is_lab', e.target.checked)} />
                    <label className="form-check-label fw-medium" htmlFor="isLab">Lab Class?</label>
                  </div>
                </div>
                {form.is_lab && (
                  <div className="col-md-4">
                    <label className="form-label">Lab / Room</label>
                    <select className="form-select" value={form.lab_name} onChange={e => f('lab_name', e.target.value)}>
                      <option value="">Select lab...</option>
                      {labs.map(l => <option key={l}>{l}</option>)}
                    </select>
                  </div>
                )}
                {!form.is_lab && (
                  <div className="col-md-3">
                    <label className="form-label">Room No.</label>
                    <input className="form-control" value={form.room} onChange={e => f('room', e.target.value)} placeholder="e.g. Room 12" />
                  </div>
                )}
              </div>
              <div className="d-flex gap-2 justify-content-end mt-3">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1" />}
                  {editing ? 'Update' : 'Add'} Period
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? <LoadingSpinner text="Loading timetable…" /> : (
        <>
          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="card">
              <div className="card-body p-0">
                {!filterClass ? (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-filter-circle d-block mb-2" style={{ fontSize: 32 }} />
                    <div style={{ fontSize: 14 }}>Select a class above to view its timetable grid</div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered mb-0" style={{ minWidth: 700, fontSize: 12 }}>
                      <thead style={{ background: '#6366f1', color: '#fff' }}>
                        <tr>
                          <th style={{ padding: '10px 12px', width: 70 }}>Period</th>
                          {DAYS.map(d => <th key={d} style={{ padding: '10px 12px', textAlign: 'center' }}>{d}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {periodNums.map(pNum => (
                          <tr key={pNum} style={{ background: pNum % 2 === 0 ? '#f8f9ff' : '#fff' }}>
                            <td style={{ fontWeight: 700, color: '#6366f1', padding: '8px 12px', textAlign: 'center', background: '#f5f3ff', verticalAlign: 'middle' }}>
                              P{pNum}
                            </td>
                            {DAYS.map(day => {
                              const cell = gridData[day]?.[pNum];
                              if (!cell) return (
                                <td key={day} style={{ padding: '6px', verticalAlign: 'middle' }}>
                                  <button className="btn btn-outline-secondary btn-sm w-100" style={{ fontSize: 10, padding: '4px', opacity: 0.5, borderStyle: 'dashed' }}
                                    onClick={() => { setForm({ ...blank, class_grade: filterClass, section: filterSection, day, period_number: pNum }); setEditing(null); setShowForm(true); }}>
                                    <i className="bi bi-plus" />
                                  </button>
                                </td>
                              );
                              const color = subjectColor(cell.subject);
                              return (
                                <td key={day} style={{ padding: '4px', verticalAlign: 'middle' }}>
                                  <div style={{ background: `${color}12`, border: `1.5px solid ${color}40`, borderRadius: 8, padding: '6px 8px', position: 'relative' }}>
                                    <div style={{ fontWeight: 700, color, fontSize: 12, lineHeight: 1.2 }}>{cell.subject}</div>
                                    <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 2 }}>
                                      <i className="bi bi-person me-1" />{cell.substitute_teacher || cell.teacher_name}
                                      {cell.substitute_teacher && <span style={{ color: '#ef4444', fontSize: 9, marginLeft: 3 }}>(Sub)</span>}
                                    </div>
                                    {cell.is_lab && <div style={{ fontSize: 9, color: '#6366f1', marginTop: 1 }}><i className="bi bi-flask me-1" />{cell.lab_name || 'Lab'}</div>}
                                    {cell.room && !cell.is_lab && <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 1 }}><i className="bi bi-geo-alt me-1" />{cell.room}</div>}
                                    <div style={{ position: 'absolute', top: 3, right: 4, display: 'flex', gap: 2 }}>
                                      <button style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11, color: '#6366f1' }} onClick={() => openEdit({ _id: cell._id, recordData: cell })}>
                                        <i className="bi bi-pencil" />
                                      </button>
                                      <button style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11, color: '#ef4444' }} onClick={() => handleDelete(cell._id)}>
                                        <i className="bi bi-trash" />
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <div className="card">
              <div className="card-body p-0">
                {records.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-calendar-x d-block mb-2" style={{ fontSize: 32 }} />
                    <div style={{ fontSize: 14 }}>No timetable entries yet.</div>
                    <button className="btn btn-primary btn-sm mt-2" onClick={() => setShowForm(true)}>Add First Period</button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0" style={{ fontSize: 12.5 }}>
                      <thead style={{ background: '#f8fafc' }}>
                        <tr>
                          <th style={{ padding: '10px 16px' }}>Class</th>
                          <th>Section</th>
                          <th>Day</th>
                          <th>Period</th>
                          <th>Subject</th>
                          <th>Faculty</th>
                          <th>Time</th>
                          <th>Room / Lab</th>
                          <th>Substitute</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {records.map(rec => {
                          const rd = rec.recordData || {};
                          const color = subjectColor(rd.subject);
                          return (
                            <tr key={rec._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '8px 16px', fontWeight: 600 }}>{rd.class_grade}</td>
                              <td><span className="badge bg-light text-dark">{rd.section}</span></td>
                              <td style={{ color: '#6366f1', fontWeight: 500 }}>{rd.day}</td>
                              <td style={{ textAlign: 'center' }}><span className="badge" style={{ background: '#ede9fe', color: '#6366f1' }}>P{rd.period_number}</span></td>
                              <td><span style={{ background: `${color}15`, color, padding: '2px 8px', borderRadius: 5, fontWeight: 600, fontSize: 11 }}>{rd.subject}</span></td>
                              <td>{rd.teacher_name}</td>
                              <td style={{ color: '#64748b', fontSize: 11 }}>{rd.start_time}{rd.end_time ? ` - ${rd.end_time}` : ''}</td>
                              <td>{rd.is_lab ? <span style={{ color: '#6366f1', fontSize: 11 }}><i className="bi bi-flask me-1" />{rd.lab_name || 'Lab'}</span> : (rd.room || '—')}</td>
                              <td>{rd.substitute_teacher ? <span style={{ color: '#ef4444', fontSize: 11 }}>{rd.substitute_teacher}</span> : '—'}</td>
                              <td>
                                <div className="d-flex gap-1">
                                  <button className="btn btn-outline-primary btn-sm" style={{ padding: '2px 7px' }} onClick={() => openEdit(rec)}><i className="bi bi-pencil" /></button>
                                  <button className="btn btn-outline-danger btn-sm" style={{ padding: '2px 7px' }} onClick={() => handleDelete(rec._id)}><i className="bi bi-trash" /></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
